/**
 * Business Environment Factors List Route
 *
 * Displays searchable, filterable list of business environment factors
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useBusinessEnvironmentFactors } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { BusinessEnvironmentFactor } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/business-environment-factors/')({
  component: BusinessEnvironmentFactorsListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { BusinessEnvironmentFactorsListPage };

interface BusinessEnvironmentFactorFilters {
  search: string;
  category: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

function BusinessEnvironmentFactorsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<BusinessEnvironmentFactorFilters>({
    search: '',
    category: '',
  });

  // Fetch business environment factors with GraphQL
  const { data, loading, error } = useBusinessEnvironmentFactors();

  // Extract business environment factors from connection
  const businessEnvironmentFactors =
    data?.businessEnvironmentFactors?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<BusinessEnvironmentFactor>({
      items: businessEnvironmentFactors,
      page,
      pageSize,
      filterFn: (businessEnvironmentFactor) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = businessEnvironmentFactor.name.toLowerCase().includes(searchLower);
          const matchesDescription = businessEnvironmentFactor.description
            ?.toLowerCase()
            .includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.category && businessEnvironmentFactor.category !== filters.category) {
          return false;
        }
        return true;
      },
      sortValue: (businessEnvironmentFactor, column) =>
        String(businessEnvironmentFactor[column as keyof BusinessEnvironmentFactor] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<BusinessEnvironmentFactor>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '40%',
      render: (businessEnvironmentFactor) => (
        <div>
          <div className="entity-list-name-cell">{businessEnvironmentFactor.name}</div>
          {businessEnvironmentFactor.description && (
            <div className="entity-list-description-cell">
              {businessEnvironmentFactor.description.substring(0, 60)}
              {businessEnvironmentFactor.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: '30%',
      render: (businessEnvironmentFactor) => (
        <span>{businessEnvironmentFactor.category || '—'}</span>
      ),
    },
    {
      key: 'label',
      label: 'Label',
      sortable: true,
      width: '30%',
      render: (businessEnvironmentFactor) => <span>{businessEnvironmentFactor.label || '—'}</span>,
    },
  ];

  const handleRowClick = (businessEnvironmentFactor: BusinessEnvironmentFactor): void => {
    navigate({ to: `/business-environment-factors/${businessEnvironmentFactor.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<BusinessEnvironmentFactorFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/business-environment-factors', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', category: '' });
    void navigate({ to: '/business-environment-factors', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/business-environment-factors', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({
      to: '/business-environment-factors',
      search: { page: 1, pageSize: newPageSize },
    });
  };

  if (error) {
    return (
      <div className="business-environment-factors-list-page">
        <h1 className="forge-typography--headline4">Business Environment Factors</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load business environment factors"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="business-environment-factors-list-page">
      <div className="business-environment-factors-header">
        <div>
          <h1 className="forge-typography--headline4">Business Environment Factors</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} business environment factors`}
          </p>
        </div>
        <PermissionGate permission="business-environment-factor:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/business-environment-factors/new' })}
          >
            Create Business Environment Factor
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search business environment factors..."
            aria-label="Search business environment factors"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by category"
          value={filters.category}
          onChange={(e) => handleFilterChange({ category: e.target.value })}
        >
          <option value="">All Categories</option>
          {Array.from(
            new Set(
              businessEnvironmentFactors
                .map((businessEnvironmentFactor) => businessEnvironmentFactor.category)
                .filter((category): category is string => Boolean(category))
            )
          ).map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        {(filters.search || filters.category) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="business-environment-factors-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={3} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(businessEnvironmentFactor) => businessEnvironmentFactor.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No business environment factors found',
                description:
                  filters.search || filters.category
                    ? 'Try adjusting your filters'
                    : 'Create your first business environment factor to get started',
                actionLabel: 'Create Business Environment Factor',
                onAction: () => navigate({ to: '/business-environment-factors/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
